/**
 * AuthModal — Sign-in / Sign-up / Link Account modal
 *
 * Two modes:
 * - "sign-in": Fresh sign-in with email or OAuth (creates new session)
 * - "link": Upgrade anonymous account (preserves user_id + saves)
 */

import React, { useState } from 'react'
import { X, Mail } from 'lucide-react'
import { useAuth } from './AuthContext'

export default function AuthModal({ isOpen, onClose, mode = 'sign-in' }) {
  const {
    signUp, signIn,
    signInWithGoogle, signInWithDiscord,
    linkEmail, linkGoogle, linkDiscord,
  } = useAuth()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [emailMode, setEmailMode] = useState('sign-up') // 'sign-up' or 'sign-in'

  if (!isOpen) return null

  const isLink = mode === 'link'
  const title = isLink ? 'Create Account' : (emailMode === 'sign-up' ? 'Create Account' : 'Sign In')

  async function handleEmailSubmit(e) {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      let result
      if (isLink) {
        // Upgrade anonymous → permanent
        result = await linkEmail(email, password)
      } else if (emailMode === 'sign-up') {
        result = await signUp(email, password)
      } else {
        result = await signIn(email, password)
      }

      if (result.error) {
        setError(result.error.message)
      } else {
        onClose()
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  async function handleGoogle() {
    setError(null)
    try {
      const result = isLink ? await linkGoogle() : await signInWithGoogle()
      if (result.error) {
        setError(result.error.message)
      }
      // OAuth redirects away — no need to close modal
    } catch (err) {
      setError(err.message)
    }
  }

  async function handleDiscord() {
    setError(null)
    try {
      const result = isLink ? await linkDiscord() : await signInWithDiscord()
      if (result.error) {
        setError(result.error.message)
      }
    } catch (err) {
      setError(err.message)
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        style={{
          backgroundColor: '#111',
          border: '1px solid #333',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '400px',
          overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid #222',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#fff' }}>
            {title}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#666',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '6px',
              display: 'flex',
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#fff'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#666'}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px' }}>
          {/* Subtitle */}
          <p style={{ color: '#888', fontSize: '14px', margin: '0 0 20px 0' }}>
            {isLink
              ? 'Link an account to save your progress across devices.'
              : 'Sign in to access your saves from any device.'}
          </p>

          {/* OAuth Buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
            <button
              onClick={handleGoogle}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                padding: '12px 16px',
                borderRadius: '8px',
                border: '1px solid #333',
                background: '#fff',
                color: '#333',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'opacity 0.15s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            >
              <svg width="18" height="18" viewBox="0 0 18 18">
                <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
                <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"/>
                <path fill="#FBBC05" d="M3.964 10.706A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"/>
                <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58z"/>
              </svg>
              Continue with Google
            </button>

            <button
              onClick={handleDiscord}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                padding: '12px 16px',
                borderRadius: '8px',
                border: 'none',
                background: '#5865F2',
                color: '#fff',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'opacity 0.15s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            >
              <svg width="18" height="14" viewBox="0 0 18 14">
                <path fill="currentColor" d="M15.248 1.169A14.85 14.85 0 0011.58 0a.056.056 0 00-.059.028c-.158.281-.333.648-.456.937a13.714 13.714 0 00-4.13 0 9.462 9.462 0 00-.463-.937A.058.058 0 006.413 0a14.81 14.81 0 00-3.668 1.169.053.053 0 00-.024.02C.398 4.521-.238 7.78.074 10.998a.062.062 0 00.024.042 14.934 14.934 0 004.494 2.272.058.058 0 00.063-.02c.346-.473.654-.972.92-1.496a.057.057 0 00-.031-.08 9.838 9.838 0 01-1.405-.67.058.058 0 01-.006-.096c.094-.071.189-.144.279-.218a.056.056 0 01.058-.008c2.946 1.345 6.135 1.345 9.046 0a.056.056 0 01.059.007c.09.074.185.148.28.22a.058.058 0 01-.005.095 9.23 9.23 0 01-1.406.669.057.057 0 00-.03.08c.27.524.578 1.023.918 1.496a.058.058 0 00.064.021 14.892 14.892 0 004.502-2.272.058.058 0 00.024-.041c.372-3.85-.623-7.195-2.638-10.15a.046.046 0 00-.023-.021zM6.023 9.007c-.879 0-1.602-.807-1.602-1.798 0-.992.71-1.799 1.602-1.799.898 0 1.615.814 1.602 1.799 0 .991-.71 1.798-1.602 1.798zm5.922 0c-.879 0-1.602-.807-1.602-1.798 0-.992.71-1.799 1.602-1.799.898 0 1.615.814 1.602 1.799 0 .991-.704 1.798-1.602 1.798z"/>
              </svg>
              Continue with Discord
            </button>
          </div>

          {/* Divider */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '20px',
          }}>
            <div style={{ flex: 1, height: '1px', background: '#333' }} />
            <span style={{ color: '#555', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              or use email
            </span>
            <div style={{ flex: 1, height: '1px', background: '#333' }} />
          </div>

          {/* Email Form */}
          <form onSubmit={handleEmailSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div>
              <label style={{ display: 'block', color: '#888', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="pilot@example.com"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '6px',
                  border: '1px solid #333',
                  background: '#1a1a1a',
                  color: '#fff',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => e.target.style.borderColor = '#22c55e'}
                onBlur={(e) => e.target.style.borderColor = '#333'}
              />
            </div>

            <div>
              <label style={{ display: 'block', color: '#888', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                placeholder="Min 6 characters"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '6px',
                  border: '1px solid #333',
                  background: '#1a1a1a',
                  color: '#fff',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => e.target.style.borderColor = '#22c55e'}
                onBlur={(e) => e.target.style.borderColor = '#333'}
              />
            </div>

            {/* Error */}
            {error && (
              <div style={{ color: '#ef4444', fontSize: '13px', padding: '8px 12px', background: '#ef444410', borderRadius: '6px' }}>
                {error}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '12px 16px',
                borderRadius: '8px',
                border: '2px solid #22c55e',
                background: loading ? '#1a1a1a' : '#22c55e20',
                color: '#22c55e',
                fontSize: '14px',
                fontWeight: '600',
                cursor: loading ? 'wait' : 'pointer',
                transition: 'all 0.15s',
                opacity: loading ? 0.6 : 1,
              }}
            >
              <Mail size={16} />
              {loading
                ? 'Please wait...'
                : isLink
                  ? 'Link Email'
                  : emailMode === 'sign-up'
                    ? 'Create Account'
                    : 'Sign In'}
            </button>
          </form>

          {/* Toggle sign-up / sign-in (not shown in link mode) */}
          {!isLink && (
            <div style={{ textAlign: 'center', marginTop: '16px' }}>
              <button
                onClick={() => {
                  setEmailMode(emailMode === 'sign-up' ? 'sign-in' : 'sign-up')
                  setError(null)
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#888',
                  fontSize: '13px',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                {emailMode === 'sign-up'
                  ? 'Already have an account? Sign in'
                  : "Don't have an account? Create one"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
